// The gate's decisions, as pure functions.
//
// A LEAF on purpose: only the small device-id helper is imported. gate.tsx pulls in
// React, the funnel screens, auth, and the depot; that graph reaches a Vite
// `?worker` module jest cannot link, so gate.tsx renders under jest only with
// the wizard screens and Bridge hooks stubbed (gate.test.tsx does exactly that
// to pin the entry latch). Keeping the decisions here is what makes them
// provable with no stubbing at all (see gate-decisions.test.ts).

import { isEmbeddedShadowDeviceId } from '#~/device/embedded-shadow-id.js';

/** Unresolved is distinct from false: only false shows the funnel. */
export type OnboardedState = 'resolving' | 'onboarded' | 'not-onboarded' | 'unknown';

/** Which screen of the funnel is showing while state is 'not-onboarded'. */
export type FunnelStep =
  | 'welcome'
  | 'bridge-concept'
  // The funnel's own permissions ask (ENG-3796). Sits BEFORE `detecting`
  // because everything after it needs the OS to have said yes: `network` gates
  // discovery, `camera` and `microphone` gate the preview on magic-moment. See
  // funnelOwnsPermissions.
  | 'permissions'
  | 'detecting'
  | 'choose-method'
  // Both of these render the REAL Add Video Source modal, differing only in the
  // panel it opens on (ENG-3796). They stay two steps because the fork before
  // them is still a real choice the user makes, and because they are still two
  // positions in the funnel's reporting.
  | 'discovery'
  | 'manual-entry'
  // NOTE: no 'camera-model'. Identifying the camera is the workflow's own
  // Source details panel now, inside the modal, so the funnel has no step of
  // its own between the modal and the connection test.
  | 'connection-test'
  | 'magic-moment'
  | 'complete';

/** A funnel screen, or null for none. */
export type FunnelScreen = FunnelStep | 'failure' | 'auto-link-failure';

/** Why a camera-less onboarding run ended. */
export const onboardingAbandonProperties = (
  action: 'skip' | 'dismiss'
): { readonly camera_added: false; readonly reason: 'explore_app' | 'dismissed' } => ({
  camera_added: false,
  reason: action === 'skip' ? 'explore_app' : 'dismissed'
});

/** Auto Link fields needed by the onboarding readiness boundary. */
export interface AutoLinkReadinessState {
  readonly status:
    | 'idle'
    | 'checking'
    | 'linking'
    | 'verifying_link'
    | 'success'
    // ENG-4052 merge: the v2 controller settles `inventory_changed` when the
    // account already owns >=1 Studio (another machine or a prior run won the
    // atomic ensure). Terminal and non-failure: Root's normal one/many path
    // owns navigation from there, so onboarding readiness must not treat it
    // as "still running".
    | 'inventory_changed'
    | 'error'
    | 'skipped';
  readonly reason?: string | undefined;
  readonly roomId?: string | undefined;
}

export interface AutoLinkOnboardingInput {
  readonly autoLinkRunsOnRoute: boolean;
  readonly state: AutoLinkReadinessState;
}

/**
 * Auto Link is the authority for the current desktop link attempt. Existing
 * agent and room records may be stale, so they cannot release onboarding while
 * this state machine is still running or has fallen back after a failure.
 */
export const isAutoLinkReadyForOnboarding = (input: AutoLinkOnboardingInput): boolean => {
  // A direct deep link does not mount AutoLinkGuard; idle there is inapplicable.
  if (!input.autoLinkRunsOnRoute && input.state.status === 'idle') return true;
  if (input.state.status === 'success') return true;
  // Terminal without a failure: the account's Studios already exist and the
  // normal one/many entry path takes over — nothing left for this row to wait
  // on. No roomId is confirmed for THIS machine, so isStudioLinkedForOnboarding
  // still requires an observed room before the link row turns green.
  if (input.state.status === 'inventory_changed') return true;
  if (input.state.status !== 'skipped') return false;

  return (
    input.state.reason === 'already_linked_local' ||
    input.state.reason === 'not_electron' ||
    input.state.reason === 'not_iris_vendor'
  );
};

/**
 * Auto Link error terminals that heal on their own. `stale_link_cleared` means
 * the controller found the sidecar still holding the PREVIOUS account's link
 * (logout → register on the same machine), unlinked it, and the sidecar is
 * rebooting the app into the fresh-install link path — no user action needed.
 * Raising the Auto Link failure screen (and its terminal `onboarding_failed`)
 * for that window told new users their Bridge could not be linked seconds
 * before it linked itself. The guard holds the benign "Setting up your
 * bridge…" overlay for the reboot; if the reboot never lands, the guard's
 * timed release to `attempts_exhausted` still reaches the failure screen.
 */
const SELF_HEALING_AUTO_LINK_ERRORS: ReadonlySet<string> = new Set(['stale_link_cleared']);

/** A failed prerequisite needs its own verdict, not a later Bridge timeout. */
export const isAutoLinkFailedForOnboarding = (input: AutoLinkOnboardingInput): boolean => {
  if (input.state.status === 'error') {
    return !SELF_HEALING_AUTO_LINK_ERRORS.has(input.state.reason ?? '');
  }
  if (input.state.status !== 'skipped') return false;

  return (
    input.state.reason === 'attempts_exhausted' || input.state.reason === 'network_unavailable'
  );
};

/**
 * Has Auto Link reached a verdict onboarding can act on? Either it is ready
 * (linked, or an accepted bypass) or it has genuinely failed. While it is still
 * running — and during a self-healing terminal, which is a reboot in flight —
 * the funnel must not OPEN: the welcome dialog sits in the browser top layer,
 * above the boot overlay, so opening it early paints "Welcome to Iris" over
 * "Loading your studio…" seconds before the Bridge has linked, and on the
 * account-switch self-heal it paints over the reboot. Entry-only: a funnel
 * that is already open keeps its own step logic (the connecting screen waits
 * for the Bridge itself).
 */
export const isAutoLinkSettledForOnboarding = (input: AutoLinkOnboardingInput): boolean =>
  isAutoLinkReadyForOnboarding(input) || isAutoLinkFailedForOnboarding(input);

/**
 * May the funnel open now? Auto Link must have settled AND the boot overlay
 * must be down (root bootstrap finished and the registry loaded — the same
 * two signals BootOverlay dismisses on), so "Welcome to Iris" never paints
 * over "Loading your studio…". An E2E probe opening the funnel bypasses both:
 * it places the gate deterministically on an already-booted app.
 * Latched by the caller once true — a later transient state must not close a
 * dialog the user is already in.
 */
export const mayOpenFunnel = (input: {
  readonly autoLink: AutoLinkOnboardingInput;
  readonly rootReady: boolean;
  readonly registryLoaded: boolean;
  readonly e2eOpen: boolean;
}): boolean =>
  input.e2eOpen ||
  (isAutoLinkSettledForOnboarding(input.autoLink) && input.rootReady && input.registryLoaded);

/**
 * Resolve the final "Link your Studio" row from the current Auto Link result.
 *
 * `useLastRoomIdPref` owns local state per hook instance. Root bootstrap can
 * write the new room through its instance while the onboarding instance keeps
 * the undefined value it read at mount. Auto Link success already contains the
 * room the server linked, so that current-run result outranks the stale hook
 * snapshot. Accepted bypasses still need a room observed by the existing room
 * signals because they do not carry a newly confirmed room id.
 *
 * Per rule: stay-in-scope (ai.knowledge/global) — this changes only the
 * onboarding display decision; it does not alter shared room persistence.
 */
export const isStudioLinkedForOnboarding = (
  input: AutoLinkOnboardingInput,
  observedRoomLinked: boolean
): boolean => {
  if (input.state.status === 'success') {
    return typeof input.state.roomId === 'string' && input.state.roomId.length > 0;
  }

  return isAutoLinkReadyForOnboarding(input) && observedRoomLinked;
};

/**
 * Whether the connecting screen's failure ceiling should be running.
 *
 * Only on the connecting screen, after Auto Link is ready. A terminal failure
 * is not readiness and must never release this timer.
 */
export const shouldArmBridgeCeiling = (
  step: FunnelStep,
  state: OnboardedState,
  autoLink: AutoLinkOnboardingInput
): boolean =>
  step === 'detecting' && state === 'not-onboarded' && isAutoLinkReadyForOnboarding(autoLink);

/** Boot slices the funnel needs to know whether the boot overlay owns the screen. */
export interface BootstrapScreenInput {
  readonly state: AutoLinkReadinessState;
  readonly isOverlayVisible: boolean;
}

/**
 * Whether the boot overlay is presenting ENG-4483's zero-Studio setup screen
 * ("Studio created / Linking / Verifying the bridge"). BootOverlay publishes
 * its actual visibility, including the first-dismissal latch. Readiness alone
 * cannot tell an in-progress first launch from post-boot connection churn.
 */
export const bootstrapOwnsLinkingScreen = (input: BootstrapScreenInput): boolean => {
  const { status } = input.state;
  const setupScreen =
    status === 'checking' ||
    status === 'linking' ||
    status === 'verifying_link' ||
    status === 'success';
  return setupScreen && input.isOverlayVisible;
};

/**
 * The single screen decision. `resolving` and `unknown` deliberately render
 * NOTHING: a returning user must never see a flash of "Welcome to Iris" while
 * the onboarded answer is still in doubt.
 *
 * `bootstrapOwnsScreen` defers EVERY funnel screen while the boot overlay is
 * showing the signed-off linking progress (bootstrapOwnsLinkingScreen). Like
 * funnelDefersToRoute this is a per-render deferral: the funnel keeps its step
 * and reappears the moment the overlay hands the screen back.
 *
 * Every screen, not just `detecting`. ENG-4483 ITB #002 scoped the deferral to
 * the connecting screen, and QA then reported (ITB 2026-09-11, new account,
 * first login) the welcome dialog painted over "Setting up your studio…"
 * while the link was still in flight: a brand-new account's activation read
 * returns `not-onboarded` at once, `step` starts at `welcome`, and the funnel
 * shell is a native <dialog> in the top layer, so nothing the overlay does can
 * cover it. The overlay latches closed after its first dismissal
 * (boot-overlay.tsx `hasDismissedOnce`), so deferring here can only ever hold
 * the funnel back on a cold boot — never blank a user who is already mid-funnel.
 *
 * The Bridge-timeout `failure` verdict is deferred with the rest: while the
 * overlay owns the screen its own boot deadline (boot-watchdog /
 * TimeoutWatchdog) is the surface that ends a stuck setup, so the funnel does
 * not paint a second verdict underneath it. An Auto Link failure still wins
 * because bootstrapOwnsLinkingScreen never claims the screen for `error`.
 *
 * Per rules: reference-rules-in-comments, think-in-systems (ai.knowledge/global).
 */
export const selectFunnelScreen = (
  state: OnboardedState,
  step: FunnelStep,
  bridgeFailed: boolean,
  autoLinkFailed: boolean,
  bootstrapOwnsScreen = false
): FunnelScreen | null => {
  if (state !== 'not-onboarded') return null;
  if (autoLinkFailed) return 'auto-link-failure';
  if (bootstrapOwnsScreen) return null;
  if (step !== 'detecting') return step;
  return bridgeFailed ? 'failure' : 'detecting';
};

/**
 * Screen while an embedded-link reload is still unresolved.
 *
 * The link reloads the studio window and the wizard step dies with it. The
 * bridge is already linked by then, so the bridge checklist is the wrong
 * screen. Hold the magic moment, which is the camera the user just linked,
 * including its "Connecting to camera..." preview. The saved note is enough
 * to paint that screen on the first frame. A pending resume outranks every
 * other answer, including "already onboarded", which would otherwise draw
 * nothing, and "Add your first camera".
 *
 * Per rule: think-in-systems (ai.knowledge/global).
 */
export const screenWhileResumePending = (
  ordinary: FunnelScreen | null,
  resumePending: boolean
): FunnelScreen | null => (resumePending ? 'magic-moment' : ordinary);

/**
 * Paint the saved camera screen on this reload.
 *
 * Onboarding runs in the desktop app only. A browser with a leftover note
 * does not get the camera screen. A route another first-run flow owns still
 * wins.
 *
 * Per rule: think-in-systems (ai.knowledge/global).
 */
export const mayPaintResume = (input: {
  pending: boolean;
  isElectron: boolean;
  pathname: string;
}): boolean =>
  input.pending &&
  isFunnelSupportedSurface({ isElectron: input.isElectron }) &&
  !funnelDefersToRoute(input.pathname);

/**
 * Whether the funnel may leave the connecting screen. Requires BOTH a genuinely
 * ready Bridge and the minimum dwell, so a healthy machine still sees the checks
 * run rather than the screen flashing past.
 */
export const mayLeaveDetecting = (
  bridgeReady: boolean,
  elapsedMs: number,
  minDwellMs: number
): boolean => bridgeReady && elapsedMs >= minDwellMs;

/**
 * Does leaving the funnel need to WRITE the onboarding flag?
 *
 * Only when the user never added a camera. The server computes
 * `onboarded = (users.onboarding_completed_at != NULL) OR (an activation
 * exists)`, and adding a camera creates that activation — so for anyone who
 * finished with a camera the answer is already true and permanent, and the
 * write is a round-trip that changes nothing.
 *
 * It still matters for the skip path: a user who explores without ever adding a
 * camera has no activation, so the flag is the only thing that stops the funnel
 * reappearing on their next launch.
 */
export const shouldMarkOnboarded = (input: { readonly cameraAdded: boolean }): boolean =>
  !input.cameraAdded;

/**
 * Does the funnel need to show its own permissions step?
 *
 * The permissions ask is not optional and it is not the funnel's to skip: the
 * OS gates `network` (discovery finds nothing without it), `camera` and
 * `microphone` (the preview on magic-moment is blank without them). So every
 * screen after `bridge-concept` depends on an answer the user has not been
 * asked for yet.
 *
 * It shipped as a MODAL on the room route (`room.tsx` renders
 * OnboardPermissionsModal whenever isElectronApp()), not as a route, so
 * `funnelDefersToRoute` cannot see it — both dialogs open on `/:roomId` and the
 * funnel draws on top. The observed result is a deadlock: the permissions
 * dialog is visible underneath and unclickable, and the funnel above it is
 * asking the user to scan a network it has no permission to scan.
 *
 * Answering `false` here is the normal case and means "nothing to ask": the
 * sandbox is disabled for this build, or the OS has already granted everything.
 * The funnel then goes straight to `detecting` and the user never sees a step
 * that would have had no content.
 *
 * `loaded` is deliberately separate from `allowed`. While the OS query is in
 * flight we do NOT claim the step — a funnel that paused on a permissions
 * screen every launch, only to find everything already granted, would be a
 * worse bug than the one this fixes.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const funnelOwnsPermissions = (input: {
  readonly sandboxDisabled: boolean;
  readonly loaded: boolean;
  readonly allowed: boolean;
}): boolean => !input.sandboxDisabled && input.loaded && !input.allowed;

/**
 * Is this a surface the first-run funnel can run on at all?
 *
 * The desktop app, and nowhere else. The funnel walks a user through connecting
 * a Bridge to a camera, which only the desktop app can do — every screen after
 * `welcome` needs an agent, a broker proxy and a linked room, and a browser has
 * none of them.
 *
 * The funnel shipped without this and the welcome modal appeared over the
 * standalone webapp's download page, which is the one page that exists to tell a
 * browser user to install the desktop app. The browser makes it deterministic
 * rather than unlucky: with no Bridge the activation read 403s, the gate records
 * `unknown`, the settle ceiling fires, and the funnel opens.
 * `use-root-bootstrap` navigates to `agent/setup` only when `!isElectronApp()`,
 * so that page is browser-only by construction.
 *
 * A correctness guard, NOT a rollout switch — the flag stays the only thing that
 * decides WHETHER the funnel runs. A PostHog flag targets a person, and one
 * person is both surfaces, so a flag can say "on for this user" but never "on
 * for their desktop and not their browser".
 *
 * A predicate rather than an `isElectronApp()` call at each site because both
 * call sites have to agree: the gate decides whether to draw the funnel, and
 * Settings decides whether to offer the button that reopens it.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const isFunnelSupportedSurface = (input: { readonly isElectron: boolean }): boolean =>
  input.isElectron;

/**
 * Routes that own the whole screen and must not have the funnel drawn over them.
 *
 * `agent/setup/prequal` is the ENG-4175 post-auth segmentation wizard. Both it
 * and this funnel target the SAME population — a first-login user who has added
 * nothing yet — and both are behind their own PostHog flag, so with both flags
 * on they fire on the same launch. The funnel's screens are a native <dialog>
 * opened with showModal(), which puts them in the browser's top layer, so
 * "both" means the welcome modal painted over the prequal questions with no
 * defined winner. Deferring is the correct direction for the same reason the
 * router places this gate inside EmailVerificationGate: the funnel walks a user
 * through first-run setup, and it must not do that until the steps that decide
 * WHO the account belongs to have finished.
 *
 * Matched as a path PREFIX so a future `agent/setup/prequal/:step` is covered
 * without a second entry here, and compared against a leading-slash-normalised
 * pathname because react-router reports `/agent/setup/prequal` while
 * `Paths.agent.setup.prequal` is declared without the slash.
 */
const FUNNEL_DEFERRED_ROUTES = ['agent/setup/prequal'] as const;

/**
 * Does another first-run flow own this route? True means render no funnel
 * screen this render, NOT that the funnel is off — the user reaches it as soon
 * as the deferred route hands the screen back.
 */
export const funnelDefersToRoute = (pathname: string): boolean => {
  const normalised = pathname.startsWith('/') ? pathname.slice(1) : pathname;

  return FUNNEL_DEFERRED_ROUTES.some(
    (route) => normalised === route || normalised.startsWith(`${route}/`)
  );
};

/**
 * Is the first-run funnel switched on for this environment?
 *
 * FAILS CLOSED, in every uncertain case: flag absent, flag false, PostHog
 * unreachable, or still resolving. An environment that has never heard of this
 * feature must behave exactly as it did before the funnel existed, and a flag
 * read that is merely SLOW must not flash a welcome screen at a returning user
 * on the way to `false`.
 *
 * That makes `loading` and `disabled` the same answer here on purpose. The cost
 * of being wrong is asymmetric: hiding the funnel from a new user for a moment
 * costs them nothing, while showing it to an existing one is a wrong screen
 * over their studio.
 */
export const isFunnelEnabled = (flag: {
  readonly enabled?: boolean | undefined;
  readonly loading?: boolean | undefined;
}): boolean => flag.loading !== true && flag.enabled === true;

/**
 * How often the gate re-asks the server whether this user is onboarded, and
 * for how long it keeps asking.
 *
 * Linking a camera does not record it. The record appears when the camera's
 * config is next written, which for an Iris-enabled camera is whenever the
 * studio has re-synced its state and run the add — observed in production
 * between 6 seconds and 2 minutes after the camera joined the room, never
 * sooner. The link path also reloads the renderer, so the gate's one read
 * lands inside that window by construction and answers "never onboarded"
 * about an account that has a camera.
 *
 * The ceiling is generous on purpose: a re-ask that stops at 30s would still
 * miss the slow quarter of that population.
 */
export const ONBOARDED_RECHECK_INTERVAL_MS = 5_000;
export const ONBOARDED_RECHECK_CEILING_MS = 150_000;

/**
 * May the gate ask the server again whether this user is onboarded?
 *
 * Three conditions, and the second is the one that protects the user.
 *
 * `not-onboarded` is the only answer worth revisiting: the others are either
 * settled or deliberately silent, and re-asking on `unknown` would undo the
 * bridge-ready settle above it.
 *
 * The step must still be `welcome`. Past that the user is IN the wizard, and a
 * server answer that arrives mid-flow must not close the dialog they are
 * working in — the same principle as the entry latch, pointed the other way.
 * A user who linked a camera and got the welcome screen again has engaged with
 * nothing, so closing it costs them a screen they did not want.
 *
 * The ceiling stops a studio that never records anything from re-asking for
 * the life of the process.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const shouldRecheckOnboarded = (input: {
  readonly state: OnboardedState;
  readonly step: FunnelStep;
  readonly elapsedMs: number;
}): boolean =>
  input.state === 'not-onboarded' &&
  input.step === 'welcome' &&
  input.elapsedMs < ONBOARDED_RECHECK_CEILING_MS;

/**
 * Where the funnel goes when the connecting screen is done.
 *
 * Normally `choose-method`: the fork between discovering cameras and typing one
 * in by hand, which is the whole point of the flow.
 *
 * But a studio ALREADY at its plan's source limit has nothing this flow can do
 * for it. That state is reachable on a first run, not just a re-run: linking an
 * Iris-enabled camera IS adding a source, so a free-tier user who linked one
 * arrives at this step holding 1 of 1 — and the funnel then offered to find
 * them a second, walked them through credentials, video and a connection test,
 * and refused at the last step (ENG-4649). Send them to the end of the flow
 * instead; they have the camera the funnel was going to get them.
 *
 * `capped` is deliberately a tri-state collapsed to a boolean by the caller,
 * and the caller must pass FALSE while the billing answer is unknown. Being
 * wrong that way costs a screen the user can skip; being wrong the other way
 * tells someone with room to spare that they are full.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const stepAfterDetecting = (capped: boolean): FunnelStep =>
  capped ? 'complete' : 'choose-method';

/** A camera as the funnel needs it to resume: enough to show and to subscribe. */
export interface ResumableCamera {
  readonly id: string;
  readonly name: string;
  readonly host: string;
}

/**
 * The camera a resumed run should finish on.
 *
 * Called after the reload that linking an embedded camera forces, with the
 * room's cameras as the depot now reports them. See resume.ts for why the
 * reload exists and why the funnel has to cross it.
 *
 * The saved agent identifies the embedded shadow device. A restarted funnel
 * can run in a room with older cameras, so their arrival must not consume this
 * note or select the wrong magic moment.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const cameraToResumeOn = (
  cameras: readonly ResumableCamera[],
  agentId: string
): ResumableCamera | undefined => {
  // Skip transient devices. An add first inserts a `tmp:`-prefixed placeholder
  // (@studio/models/device TransientPrefix) that later resolves to the
  // permanent device id. Resuming on the placeholder mounts the viewer with an
  // id that never maps to a LiveKit track, so the preview sits on
  // "Connecting…" forever while the dashboard (which uses the resolved id)
  // plays. Wait for the real device (ENG-4649, confirmed live 2026-09-16).
  return cameras.find(
    (camera) => !camera.id.startsWith('tmp:') && isEmbeddedShadowDeviceId(camera.id, agentId)
  );
};

export type ResumeExit = 'tick' | 'skip' | 'dismiss' | 'restart' | 'failed-link';

export type ResumeAction = 'keep' | 'consume' | 'drop';

/**
 * What to do with the note written before an embedded-link reload.
 *
 * Leaving the run drops it: skip, dismiss, restart onboarding, or a failed
 * link. The caller counts only the saved agent's settled camera, so an older
 * camera cannot consume the note or count as a removal.
 *
 * Per rule: think-in-systems (ai.knowledge/global).
 */
export const decideResume = (input: {
  pending: boolean;
  settledCount: number;
  sawSettled: boolean;
  exit: ResumeExit;
}): ResumeAction => {
  if (!input.pending) return 'keep';
  if (input.exit !== 'tick') return 'drop';
  if (input.settledCount === 1) return 'consume';
  if (input.sawSettled && input.settledCount === 0) return 'drop';
  return 'keep';
};

/**
 * Close the camera screen after the note has already been consumed.
 *
 * The note is gone by then, so removing the camera must close the screen on
 * its own. An empty room before the device id has ever been seen is the wait
 * for the link to land, not a removal.
 *
 * Per rule: think-in-systems (ai.knowledge/global).
 */
export const shouldCloseShownCamera = (input: {
  deviceId: string | undefined;
  roomIds: readonly string[];
  seenInRoom: boolean;
}): boolean => {
  if (input.deviceId === undefined) return false;
  if (input.roomIds.includes(input.deviceId)) return false;
  return input.seenInRoom;
};
